/**
 * QuotacaoAereoView.js
 *
 * Responsabilidade: Renderizar cotação exclusivamente aérea para o cliente.
 *
 * Modelo 1 — 1 opção: ficha única, sem fluxo de telas.
 * Modelo 2 — 2+ opções: mesmo layout com abas no topo.
 *   Abas mostram diferença de preço em relação à opção base (índice 0).
 *
 * Regras:
 * - Usa tokens DSC: navy #09077d / teal #50cfad / Bodoni Moda / Raleway
 * - Hero com destination_image_url do Supabase
 * - Sem narrativa, sem fluxo de telas, sem storytelling
 * - Tracking de engagement mantido (recebe quote.id de fora)
 * - Fallback seguro em todos os campos
 */

import React, { useState } from 'react';
import { getAirline } from '../../config/airlines';
import BookingModal from '../BookingModal';

// ─── Tokens ──────────────────────────────────────────────────────────────────

const T = {
  navy:       '#09077d',
  navyDark:   '#05043d',
  teal:       '#50cfad',
  silver:     '#b8bade',
  slate:      '#8b8ec4',
  white:      '#f0f0f8',
  warm:       '#ffbc15',
  fontDisplay: "'Bodoni Moda', Georgia, serif",
  fontBody:    "'Raleway', -apple-system, sans-serif",
};

const BG = `linear-gradient(175deg, ${T.navy} 0%, ${T.navyDark} 100%)`;

// ─── Utilitários ─────────────────────────────────────────────────────────────

function formatBRL(value) {
  if (!value && value !== 0) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2
  }).format(value);
}

function formatDiffBRL(diff) {
  if (diff === 0) return null;
  const sign = diff < 0 ? '−' : '+';
  return `${sign} ${formatBRL(Math.abs(diff))}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' });
  } catch { return dateStr; }
}

// ─── BaggageIcons ─────────────────────────────────────────────────────────────

function BaggageIcons({ baggage = {} }) {
  const hasPersonal  = baggage.personal_item  !== false;
  const hasCarryOn   = !!(baggage.carry_on);
  const hasChecked   = (baggage.checked_bags || 0) > 0;

  const iconStyle = (on) => ({
    width: '36px', height: '36px', borderRadius: '8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: on ? 'rgba(80,207,173,0.15)' : 'rgba(255,255,255,0.06)',
  });

  const stroke = (on) => on ? T.teal : 'rgba(255,255,255,0.2)';

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      {/* Item pessoal */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <div style={iconStyle(hasPersonal)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke={stroke(hasPersonal)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="6" y="9" width="12" height="12" rx="2"/>
            <path d="M9 9V7a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            <path d="M6 13h12"/>
          </svg>
        </div>
        <span style={{ fontSize: '9px', color: T.slate, textAlign: 'center', lineHeight: 1.2 }}>item<br/>pessoal</span>
      </div>

      {/* Bagagem de mão */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <div style={iconStyle(hasCarryOn)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={stroke(hasCarryOn)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="8" width="14" height="13" rx="2"/>
            <path d="M8 8V6a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/>
            <path d="M5 13h14"/>
          </svg>
        </div>
        <span style={{ fontSize: '9px', color: T.slate, textAlign: 'center', lineHeight: 1.2 }}>bagagem<br/>de mão</span>
      </div>

      {/* Despachada */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <div style={iconStyle(hasChecked)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke={stroke(hasChecked)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="7" width="16" height="14" rx="2"/>
            <path d="M7 7V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v2"/>
            <path d="M4 13h16"/>
          </svg>
        </div>
        <span style={{ fontSize: '9px', color: T.slate, textAlign: 'center', lineHeight: 1.2 }}>despachada</span>
      </div>
    </div>
  );
}

// ─── FlightCard ───────────────────────────────────────────────────────────────

function FlightCard({ block, label }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!block) return null;

  const details  = block.details || {};
  const segments = details.segments || [];
  const first    = segments[0] || {};
  const last     = segments[segments.length - 1] || first;
  const baggage  = details.baggage || {};

  const airlineId  = details.airline || first.airline_code || first.airline || '';
  const airline    = getAirline(airlineId);
  const originCode = first.origin      || details.origin      || '---';
  const destCode   = last.destination  || details.destination || '---';
  const originCity = first.origin_city || originCode;
  const destCity   = last.destination_city || destCode;
  const depTime    = first.departure_time || details.departure_time || '--:--';
  const arrTime    = last.arrival_time    || details.arrival_time   || '--:--';
  const isDirect   = details.is_direct !== false && segments.length <= 1;
  const duration   = details.duration || '';
  const fareFamily = details.fare_family || baggage.fare_family || '';
  const dateStr    = block.date ? formatDate(block.date) : '';
  const hasConn    = segments.length > 1;

  const calcConnection = (seg, nextSeg) => {
    if (!seg.arrival_time || !nextSeg.departure_time) return '';
    const [ah, am] = seg.arrival_time.split(':').map(Number);
    const [dh, dm] = nextSeg.departure_time.split(':').map(Number);
    let diff = (dh * 60 + dm) - (ah * 60 + am);
    if (diff < 0) diff += 24 * 60;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return h > 0 ? `${h}h${m > 0 ? m + 'min' : ''}` : `${m}min`;
  };

  // Linha de trecho — mesmo padrão visual contraído e expandido
  const SegRow = ({ depT, depCode, depCity, arrT, arrCode, arrCity, airCode, flightNum, conn, airportChange }) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

        {/* Bloco esquerdo — vertical */}
        <div style={{ width: '80px', minHeight: '52px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <span style={{ fontSize: '22px', fontWeight: 700, color: T.white, fontFamily: 'monospace', lineHeight: 1 }}>{depT}</span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: T.slate, marginTop: '2px' }}>{depCode}</span>
          <span style={{ fontSize: '10px', color: T.slate, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80px' }}>{depCity !== depCode ? depCity : ''}</span>
        </div>

        {/* Linha central */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
          {airCode && <span style={{ fontSize: '10px', color: T.slate, fontFamily: 'monospace' }}>{airCode}{flightNum}</span>}
          <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.2)' }} />
          {!airCode && duration && <span style={{ fontSize: '9px', color: T.slate }}>{duration}</span>}
        </div>

        {/* Bloco direito — vertical, alinhado à direita */}
        <div style={{ width: '80px', minHeight: '52px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end' }}>
          <span style={{ fontSize: '22px', fontWeight: 700, color: T.white, fontFamily: 'monospace', lineHeight: 1 }}>{arrT}</span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: T.slate, marginTop: '2px' }}>{arrCode}</span>
          <span style={{ fontSize: '10px', color: T.slate, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80px', textAlign: 'right' }}>{arrCity !== arrCode ? arrCity : ''}</span>
        </div>

      </div>

      {/* Badge conexão */}
      {conn && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px',
          padding: '3px 8px', margin: '3px 0',
          background: airportChange ? 'rgba(220,38,38,0.12)' : 'rgba(80,207,173,0.08)',
          borderRadius: '6px', fontSize: '10px',
          color: airportChange ? '#fca5a5' : T.teal, fontWeight: 600,
        }}>
          {airportChange ? `⚠️ Troca de aeroporto · ${airportChange}` : `Conexão em ${arrCode}`}
          <span style={{ color: T.slate, fontWeight: 400 }}>· {conn} de espera</span>
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      background: 'rgba(255,255,255,0.06)',
      border: '0.5px solid rgba(255,255,255,0.12)',
      borderRadius: '14px', padding: '14px',
    }}>
      {/* Label */}
      <p style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.teal, margin: '0 0 10px' }}>
        {label}{dateStr ? ` — ${dateStr}` : ''}
      </p>

      {/* Resumo contraído — visível apenas quando colapsado */}
      {!expanded && (
        <SegRow
          depT={depTime} depCode={originCode} depCity={originCity}
          arrT={arrTime} arrCode={destCode}   arrCity={destCity}
          airCode={null} flightNum={null}
        />
      )}

      {/* Badge de paradas + botão expandir */}
      {hasConn && (
        <div
          onClick={() => setExpanded(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            marginTop: '6px', cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: '10px', fontWeight: 600, color: T.warm }}>
            {segments.length - 1} conexão{segments.length - 1 > 1 ? 'ões' : ''}
          </span>
          {duration && <span style={{ fontSize: '10px', color: T.slate }}>· {duration}</span>}
          <span style={{ fontSize: '10px', color: T.slate, marginLeft: 'auto' }}>
            {expanded ? '▲ menos' : '▼ detalhes'}
          </span>
        </div>
      )}

      {/* Expandido — segmentos individuais */}
      {hasConn && expanded && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '0.5px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {segments.map((seg, idx) => (
            <SegRow
              key={idx}
              depT={seg.departure_time} depCode={seg.origin}       depCity={seg.origin_city || seg.origin}
              arrT={seg.arrival_time}   arrCode={seg.destination}  arrCity={seg.destination_city || seg.destination}
              airCode={seg.airline_code || seg.airline}
              flightNum={seg.flight_number}
              conn={idx < segments.length - 1 ? calcConnection(seg, segments[idx + 1]) : null}
              airportChange={seg.airport_change ? seg.airport_change_detail : null}
            />
          ))}
        </div>
      )}

      {/* Cia + bagagem */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginTop: '12px', paddingTop: '12px',
        borderTop: '0.5px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {airline?.logo ? (
            <div style={{ width: '26px', height: '26px', borderRadius: '6px', background: 'white', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={airline.logo} alt={airline.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '2px' }} />
            </div>
          ) : (
            <div style={{ width: '26px', height: '26px', borderRadius: '6px', background: 'rgba(80,207,173,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: T.teal }}>
              {(airlineId || '?').slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <p style={{ fontSize: '12px', fontWeight: 600, color: T.white, margin: 0 }}>{airline?.name || airlineId || 'Companhia'}</p>
            {fareFamily && <p style={{ fontSize: '10px', color: T.slate, margin: '1px 0 0' }}>{fareFamily}</p>}
          </div>
        </div>
        <BaggageIcons baggage={baggage} />
      </div>
    </div>
  );
}

// ─── PriceBreakdown ───────────────────────────────────────────────────────────

function PriceBreakdown({ quote }) {
  const finalPrice = quote.final_price || 0;
  const adults     = quote.adults || 1;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.06)',
      border: '0.5px solid rgba(255,255,255,0.12)',
      borderRadius: '14px', padding: '14px',
    }}>
      <p style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.teal, margin: '0 0 10px' }}>
        Resumo do valor
      </p>

      {/* Total */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '8px', paddingTop: '10px', borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
        <p style={{ fontSize: '13px', fontWeight: 600, color: T.white, margin: 0 }}>Total</p>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '22px', fontWeight: 700, color: T.teal, margin: 0, lineHeight: 1 }}>
            {formatBRL(finalPrice)}
          </p>
          <p style={{ fontSize: '10px', color: T.slate, margin: '2px 0 0' }}>
            {adults} adulto{adults > 1 ? 's' : ''} · ida + volta
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Tabs (Modelo 2) ──────────────────────────────────────────────────────────

function OptionTabs({ options, activeIndex, onChange }) {
  const basePrice = options[0]?.final_price || 0;

  return (
    <div style={{ display: 'flex', gap: '8px', padding: '16px 20px 0' }}>
      {options.map((opt, i) => {
        const isActive = i === activeIndex;
        const diff     = (opt.final_price || 0) - basePrice;
        const diffStr  = i === 0 ? null : formatDiffBRL(diff);
        const isBase   = i === 0;

        return (
          <div
            key={opt.id || i}
            onClick={() => onChange(i)}
            style={{
              flex: 1, padding: '10px 8px', borderRadius: '12px',
              border: isActive ? `1.5px solid ${T.teal}` : '0.5px solid rgba(255,255,255,0.12)',
              background: isActive ? 'rgba(80,207,173,0.1)' : 'rgba(255,255,255,0.04)',
              cursor: 'pointer', textAlign: 'center',
            }}
          >
            {/* Badge */}
            <div style={{ marginBottom: '4px' }}>
              <span style={{
                display: 'inline-block', fontSize: '9px', fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '2px 7px', borderRadius: '20px',
                background: isBase ? 'rgba(80,207,173,0.2)' : 'rgba(255,255,255,0.08)',
                color: isBase ? T.teal : T.slate,
              }}>
                {isBase ? 'base' : 'alternativa'}
              </span>
            </div>

            {/* Data do primeiro bloco de voo */}
            <p style={{ fontSize: '13px', fontWeight: 700, color: T.white, margin: '0 0 2px', lineHeight: 1.2 }}>
              {opt.blocks?.[0]?.date ? formatDate(opt.blocks[0].date) : `Opção ${i + 1}`}
            </p>

            {/* Diferença de preço */}
            {diffStr ? (
              <p style={{
                fontSize: '12px', fontWeight: 600, margin: '4px 0 0',
                color: diff < 0 ? T.teal : T.warm,
              }}>
                {diffStr}
              </p>
            ) : (
              <p style={{ fontSize: '11px', color: T.slate, margin: '4px 0 0' }}>
                referência
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── QuotacaoAereoView (principal) ────────────────────────────────────────────

export default function QuotacaoAereoView({ quote, options = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);

  const allOptions  = options.length > 0 ? options : [quote];
  const hasMultiple = allOptions.length > 1;
  const activeQuote = allOptions[activeIndex] || quote;

  const confirmedKey = `confirmed_${quote?.public_link}_${activeQuote?.id}`;
  const [confirmed, setConfirmed] = useState(() => {
    try { return !!localStorage.getItem(confirmedKey); } catch { return false; }
  });
  const [showBooking, setShowBooking] = useState(false);
  const [bookingOffer, setBookingOffer] = useState(null);
  const [bookingPassengers, setBookingPassengers] = useState([]);
  const [searching, setSearching] = useState(false);

  const API_URL = 'https://cotacoes.dsctravel.com.br/api';

  const handleConfirm = async () => {
    if (confirmed || searching) return;
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/c/${quote.public_link}/search-for-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok || !data.available) {
        alert(data.reason || 'Voo não disponível. Entre em contato com a DSC Travel.');
        return;
      }
      if (data.price_changed) {
        const ok = window.confirm(
          `O preço foi atualizado para R$ ${data.new_price?.toFixed(2).replace('.', ',')}. Deseja continuar?`
        );
        if (!ok) return;
      }
      setBookingOffer(data.offer);
      const contact = data.contact || {};
      setBookingPassengers(contact.first_name ? [{
        first_name:      contact.first_name || '',
        last_name:       contact.last_name  || '',
        birth_date:      contact.birth_date || '',
        document_number: contact.cpf        || '',
        gender:          contact.gender     || '',
        email:           contact.email      || '',
        phone:           contact.phone      || '',
      }] : []);
      setShowBooking(true);
    } catch (e) {
      alert('Erro ao verificar disponibilidade. Tente novamente.');
    } finally {
      setSearching(false);
    }
  };

  const blocks        = activeQuote.blocks || [];
  const flightBlocks  = blocks.filter(b => b.type === 'flight');
  const outbound      = flightBlocks.find(b => b.details?.direction === 'outbound') || flightBlocks[0];
  const returnFlight  = flightBlocks.find(b => b.details?.direction === 'return')   || flightBlocks[1];

  // Rota para o título — nomes de cidade completos
  // Hierarquia: origin_city do segmento → destination da cotação
  const firstSeg   = outbound?.details?.segments?.[0] || {};
  const lastSeg    = outbound?.details?.segments?.slice(-1)[0] || {};
  const originName = firstSeg.origin_city || outbound?.details?.origin_city || firstSeg.origin || '';
  const destName   = lastSeg.destination_city || outbound?.details?.destination_city || activeQuote.destination || '';
  const rota       = originName && destName
    ? `${originName} → ${destName}`
    : destName || originName || 'Passagem aérea';

  const heroImage   = activeQuote.destination_image_url || null;
  const clientName  = (quote.client_name || '').split(' ')[0];

  return (
    <div style={{
      minHeight: '100vh', width: '100%',
      background: T.navyDark, fontFamily: T.fontBody,
      color: T.white, display: 'flex', flexDirection: 'column',
    }}>

      {/* Header DSC */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '28px', height: '28px', borderRadius: '8px', background: T.teal,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: T.fontDisplay, fontSize: '14px', fontWeight: 600, color: T.navyDark,
          }}>D</div>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.04em' }}>
            DSC TRAVEL
          </span>
        </div>
      </div>

      {/* Hero */}
      <div style={{ position: 'relative', height: '180px', overflow: 'hidden', margin: '14px 20px 0', borderRadius: '14px' }}>
        {heroImage ? (
          <img
            src={heroImage} alt={rota}
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '14px', display: 'block' }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', borderRadius: '14px', background: 'rgba(255,255,255,0.04)' }} />
        )}
        {/* Gradiente só na metade inferior — protege o texto sem embaçar a foto */}
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '14px',
          background: 'linear-gradient(to bottom, transparent 45%, rgba(5,4,61,0.98) 75%, rgba(5,4,61,0.98) 100%)',
        }} />
        <div style={{ position: 'absolute', bottom: '16px', left: '16px' }}>
          <p style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.teal, margin: '0 0 4px' }}>
            Passagem aérea · {activeQuote.adults || 1} adulto{(activeQuote.adults || 1) > 1 ? 's' : ''}
          </p>
          <h1 style={{
            fontFamily: T.fontDisplay, fontSize: '24px', fontWeight: 400,
            letterSpacing: '0.03em', textTransform: 'uppercase', color: T.white,
            margin: 0, lineHeight: 1.1,
          }}>
            {rota}
          </h1>
        </div>
      </div>

      {/* Saudação */}
      <p style={{ fontSize: '12px', fontWeight: 300, color: T.silver, lineHeight: 1.65, margin: '14px 20px 0' }}>
        {clientName ? `${clientName}, encontramos` : 'Encontramos'} a melhor opção para a sua viagem. Confira os detalhes abaixo.
      </p>

      {/* Abas — Modelo 2 */}
      {hasMultiple && (
        <OptionTabs
          options={allOptions}
          activeIndex={activeIndex}
          onChange={setActiveIndex}
        />
      )}

      {/* Corpo */}
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
        {outbound && <FlightCard block={outbound} label="Voo de ida" />}
        {returnFlight && <FlightCard block={returnFlight} label="Voo de volta" />}
        <PriceBreakdown quote={activeQuote} />

        {/* Validade */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '10px 14px',
          background: 'rgba(255,188,21,0.08)',
          border: '0.5px solid rgba(255,188,21,0.25)',
          borderRadius: '10px',
        }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: T.warm, flexShrink: 0 }} />
          <p style={{ fontSize: '11px', color: T.warm, fontWeight: 500, margin: 0 }}>
            Preço válido por 24 horas · consulte disponibilidade
          </p>
        </div>
      </div>

      {/* CTA */}
      <div style={{ padding: '4px 20px 40px' }}>
        <button
          onClick={handleConfirm}
          disabled={confirmed || searching}
          style={{
            display: 'block', width: '100%', padding: '16px 24px',
            borderRadius: '50px',
            background: confirmed ? 'rgba(80,207,173,0.2)' : T.teal,
            color: confirmed ? T.teal : T.navyDark,
            fontFamily: T.fontBody,
            fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em',
            textAlign: 'center', border: confirmed ? `1.5px solid ${T.teal}` : 'none',
            cursor: confirmed ? 'default' : 'pointer',
            textTransform: 'uppercase',
            transition: 'all 0.2s',
          }}>
          {confirmed
            ? '✓ Reserva confirmada'
            : searching
              ? 'Verificando disponibilidade...'
              : hasMultiple
                ? `Quero esta opção — ${formatBRL(activeQuote.final_price)}`
                : 'Reservar'
          }
        </button>
      </div>

      {/* Modal de reserva */}
      {showBooking && bookingOffer && (
        <BookingModal
          outbound={bookingOffer}
          returnFlight={null}
          adults={quote.adults || 1}
          children={quote.children || 0}
          infants={quote.infants || 0}
          origin={(bookingOffer.outbound?.segments?.[0]?.origin?.code) || ''}
          destination={(bookingOffer.outbound?.segments?.slice(-1)[0]?.destination?.code) || ''}
          departureDate={quote.start_date || ''}
          returnDate={quote.end_date || null}
          isPublic={true}
          contactId={quote.contact_id}
          defaultPassengers={bookingPassengers}
          onClose={(success) => {
            setShowBooking(false);
            if (success) {
              try { localStorage.setItem(confirmedKey, '1'); } catch {}
              setConfirmed(true);
            }
          }}
          onSuccess={() => {
            try { localStorage.setItem(confirmedKey, '1'); } catch {}
            setConfirmed(true);
            setShowBooking(false);
          }}
        />
      )}

    </div>
  );
}
